// src/components/StageBadge.jsx
//
// Pill displaying a stage label. Color comes from STAGES; the visual
// is identical to the legacy monolith's StageBadge.
//
// LP1.2 Gate 1 — opt-in inline editing.
//   - When `canWrite` is false (default), renders the legacy
//     display-only `<div>` pill exactly as before. LocationCard +
//     other display surfaces stay byte-identical.
//   - When `canWrite` is true AND `onChange` is provided, renders a
//     `<button>` trigger with a small `▾` chevron. Clicking opens a
//     dropdown menu listing every stage in the canonical STAGES enum.
//     Selecting an option calls `await onChange(newKey)`; the
//     component shows a busy state during the await, closes on
//     success, and surfaces an inline error message on failure
//     (menu stays open so the user can retry or dismiss).
//   - A11y: Escape closes + returns focus to the trigger. ArrowDown /
//     ArrowUp move focus between option buttons. Enter selects
//     (native button behavior). Click-outside closes.
//
// Server contract: the existing `updateStage(id, stage)` client method
// validates the key against VALID_STAGE before PATCHing; this
// component does not need to re-validate.

import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { STAGES, T } from "../app/tokens.js";
import { S } from "../app/styles.js";

const STAGE_KEYS = Object.keys(STAGES);

export function StageBadge({ stage, canWrite = false, onChange, pillStyle }) {
  const stageInfo = STAGES[stage] || STAGES.closed;
  const editable = canWrite && typeof onChange === "function";

  // Display-only path — canonical squarish pill shape (borderRadius 6, no border).
  if (!editable) {
    return (
      <div
        data-testid="stage-badge"
        style={{
          display:        "inline-flex",
          alignItems:     "center",
          justifyContent: "center",
          borderRadius:   6,
          height:         22,
          lineHeight:     1,
          padding:        "0 10px",
          fontSize:       T.fsXs,
          fontWeight:     T.fwSemibold,
          background:     stageInfo.bg,
          color:          stageInfo.color,
          minWidth:       90,
          whiteSpace:     "nowrap",
          ...pillStyle,
        }}
      >
        {stageInfo.label}
      </div>
    );
  }

  return <EditableStageBadge stage={stage} onChange={onChange} stageInfo={stageInfo} />;
}

function EditableStageBadge({ stage, onChange, stageInfo }) {
  const [open, setOpen]     = useState(false);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, maxHeight: 0 });
  const triggerRef          = useRef(null);
  const menuRef             = useRef(null);
  const menuId              = useId();

  // Position the portalled menu against the trigger's viewport rectangle.
  // Capture scroll so nested scrolling containers keep the menu attached.
  useLayoutEffect(() => {
    if (!open) return undefined;

    function recomputePosition() {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;

      const triggerRect = trigger.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const margin = 8;
      const gap = 4;
      const width = menuRect.width || 160;
      const height = menuRect.height || 0;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const maxLeft = Math.max(margin, viewportWidth - width - margin);
      const left = Math.min(Math.max(margin, triggerRect.left), maxLeft);
      const roomBelow = Math.max(0, viewportHeight - margin - triggerRect.bottom - gap);
      const roomAbove = Math.max(0, triggerRect.top - margin - gap);

      let top;
      let maxHeight;
      if (height <= roomBelow || roomBelow >= roomAbove) {
        top = triggerRect.bottom + gap;
        maxHeight = roomBelow;
      } else {
        maxHeight = roomAbove;
        top = Math.max(margin, triggerRect.top - gap - Math.min(height, maxHeight));
      }

      setMenuPosition((current) => (
        current.top === top && current.left === left && current.maxHeight === maxHeight
          ? current
          : { top, left, maxHeight }
      ));
    }

    recomputePosition();
    window.addEventListener("scroll", recomputePosition, true);
    window.addEventListener("resize", recomputePosition);
    return () => {
      window.removeEventListener("scroll", recomputePosition, true);
      window.removeEventListener("resize", recomputePosition);
    };
  }, [open, stage, error]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Escape closes + returns focus to the trigger.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus the active option when the menu opens (a11y default landing).
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      const active = menuRef.current?.querySelector(`[data-testid="stage-badge-option-${stage}"]`);
      (active || menuRef.current?.querySelector("button"))?.focus();
    });
  }, [open, stage]);

  function onMenuKeyDown(e) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const buttons = Array.from(menuRef.current?.querySelectorAll("button[data-testid^='stage-badge-option-']") || []);
    const idx = buttons.indexOf(document.activeElement);
    if (idx < 0) return;
    e.preventDefault();
    const next = e.key === "ArrowDown" ? (idx + 1) % buttons.length : (idx - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  }

  async function handleSelect(newKey) {
    if (busy || newKey === stage) {
      // No-op selections close the menu without a network round-trip.
      if (newKey === stage) {
        setOpen(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onChange(newKey);
      setBusy(false);
      setOpen(false);
      requestAnimationFrame(() => triggerRef.current?.focus());
    } catch (err) {
      setBusy(false);
      setError(mapError(err));
    }
  }

  const menu = open && (
    <div
      ref={menuRef}
      id={menuId}
      role="listbox"
      data-testid="stage-badge-menu"
      onKeyDown={onMenuKeyDown}
      style={{
        position: "fixed",
        top: menuPosition.top,
        left: menuPosition.left,
        zIndex: 1300,
        boxSizing: "border-box",
        minWidth: "min(160px, calc(100vw - 16px))",
        maxWidth: "calc(100vw - 16px)",
        maxHeight: menuPosition.maxHeight ? `${menuPosition.maxHeight}px` : "calc(100vh - 16px)",
        overflowY: "auto",
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 6,
        boxShadow: T.cardShadow,
        padding: 4,
      }}
    >
      {STAGE_KEYS.map((key) => {
        const info   = STAGES[key];
        const active = key === stage;
        return (
          <button
            key={key}
            type="button"
            role="option"
            aria-selected={active}
            data-testid={`stage-badge-option-${key}`}
            onClick={() => handleSelect(key)}
            disabled={busy}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              padding: "6px 10px",
              background: active ? T.bg : "transparent",
              border: "none",
              borderRadius: 4,
              cursor: busy ? "wait" : "pointer",
              fontSize: T.fsSm,
              fontWeight: active ? 600 : 500,
              color: T.dark,
              textAlign: "left",
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: info.color, flexShrink: 0 }} />
              {info.label}
            </span>
            {active && <span aria-hidden="true" style={{ color: T.mid, fontSize: T.fsXs }}>✓</span>}
          </button>
        );
      })}
      {error && (
        <div
          data-testid="stage-badge-error"
          role="alert"
          style={{ padding: "6px 10px", fontSize: T.fsXs, color: T.red }}
        >
          {error}
        </div>
      )}
    </div>
  );

  return (
    <>
      <span style={{ position: "relative", display: "inline-block" }}>
        <button
          ref={triggerRef}
          type="button"
          data-testid="stage-badge"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={menuId}
          disabled={busy}
          onClick={() => setOpen((o) => !o)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            borderRadius: 6,
            padding: "2px 10px",
            fontSize: T.fsXs,
            fontWeight: T.fwSemibold,
            letterSpacing: 0.3,
            lineHeight: 1,
            whiteSpace: "nowrap",
            background: stageInfo.bg,
            color: stageInfo.color,
            border: "none",
            cursor: busy ? "wait" : "pointer",
            gap: 4,
            opacity: busy ? 0.6 : 1,
          }}
        >
          {stageInfo.label}
          <span aria-hidden="true" style={{ fontSize: T.fsXxxs, opacity: 0.7 }}>▾</span>
        </button>
      </span>
      {menu && typeof document !== "undefined" ? createPortal(menu, document.body) : null}
    </>
  );
}

// Maps the typed errors thrown by updateStage into short user-facing
// strings. Mirrors the LocationDetail delete handler's mapping shape so
// the family of inline-edit affordances surfaces consistent copy.
function mapError(err) {
  const name = err?.name;
  if (name === "AuthError")       return "You don't have permission to change stage.";
  if (name === "ValidationError") return err.message || "Invalid stage.";
  if (name === "NotFoundError")   return "Location not found.";
  if (name === "NetworkError")    return "Network error. Please try again.";
  return "Couldn't update stage. Please try again.";
}
